<template>
  <div id="profile-edit-layout" class="min-h-screen bg-grey-surface-subdued pb-32">
    <generic-error v-if="loadError" class="mt-48" />
    <poppins-spinner v-else-if="loadingProfile" :width="48" :height="48" class="mx-auto mt-48" />
    <div v-else>
      <alert-banner v-if="errorUpdating || successUpdating" :status="bannerInfo.status" class="mb-24 mt-0">
        {{ bannerInfo.text }}
      </alert-banner>
      <router-view :key="$route.fullPath" class="md:mt-32" />
    </div>
  </div>
</template>

<script lang="ts">
import { profileQueryMap } from '@/graphql/provider/profile/queries';
import { careTypeRouteToGraphqlKey, routeCareTypeToFormattedCareType } from './lib/careTypeUtils';
import { UPSERT_PROFILE_SITTER } from '@/graphql/profile/mutations';
import GenericError from '@/components/generic_error/GenericError.vue';
import PoppinsSpinner from '@/components/spinner/Spinner.vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import helperStatus from '@/helpers/status';
import { serviceRateToServiceKey } from './__types__/PetCareProfileTypes';
import { ratesToServiceRateFormEntries, serviceRateFormEntriesToUpsert } from './lib/companionCareRateUtils';

const uniqueStringList = (values: unknown): string[] =>
  Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));

const CHILD_CARE_ARRAY_KEYS = new Set([
  'ageGroups',
  'qualifications',
  'languagesSpoken',
  'additionalServices',
  'infantCareServices',
  'homeworkHelpType',
  'homeworkHelpSubjects',
  'specialNeedsRecipientAgeGroups',
  'specialNeedsCareExperience',
  'specialNeedsConditionExperience',
  'specialNeedsServiceExperience',
]);

const CHILD_CARE_UPSERT_KEYS = [
  'bio',
  'comfortableWithPets',
  'distanceWillingToTravel',
  'education',
  'ageGroups',
  'languagesSpoken',
  'qualifications',
  'yearsExperience',
  'paymentRate',
  'maxNumberOfChildren',
  'accessToTransportation',
  'validDriversLicense',
  'homeworkHelp',
  'specialNeeds',
  'increasePerChildRate',
  'additionalServices',
  'infantCare',
  'infantCareExperience',
  'infantCareServices',
  'homeworkHelpType',
  'homeworkHelpSubjects',
  'childWithSpecialNeeds',
  'specialNeedsRecipientAgeGroups',
  'specialNeedsYearsExperience',
  'specialNeedsCareExperience',
  'specialNeedsConditionExperience',
  'specialNeedsServiceExperience',
] as const;

const COMPANION_CARE_UPSERT_KEYS = [
  'bio',
  'comfortableWithPets',
  'distanceWillingToTravel',
  'education',
  'ageGroups',
  'experience',
  'languagesSpoken',
  'qualifications',
  'serviceRates',
  'yearsExperience',
  'services',
] as const;

export default {
  name: 'ProfileEditLayout',
  components: {
    GenericError,
    PoppinsSpinner,
    AlertBanner,
  },
  provide() {
    // Use a getter so children get the reactive computed value
    const self = this;
    return {
      get profileEditContext() {
        return self.profileEditContext;
      },
    };
  },
  data() {
    return {
      profileData: null as any,
      loadingProfile: true,
      loadError: false,
      errorUpdating: false,
      errorMessage: '',
      successUpdating: false,
      helperStatus,
    };
  },
  computed: {
    careType(): string {
      return this.$route.params.care_type;
    },
    bannerInfo(): any {
      if (this.errorUpdating) {
        return {
          status: helperStatus.Error,
          text: this.errorMessage.length ? this.errorMessage : 'There was an error saving your changes. Please try again.',
        };
      }
      if (this.successUpdating) {
        return {
          status: helperStatus.Valid,
          text: 'Your profile has successfully been updated!',
        };
      }
      return null;
    },
    accountId(): string {
      return this.$store.getters['account/getAccount']?.id || '';
    },
    graphqlCareType(): string {
      return careTypeRouteToGraphqlKey[this.careType as keyof typeof careTypeRouteToGraphqlKey];
    },
    profileQuery() {
      return profileQueryMap[this.careType];
    },
    profileEditContext() {
      return {
        careType: this.$route.params.care_type,
        graphqlCareType: this.graphqlCareType,
        sitterUuid: this.accountId,
        loading: this.loadingProfile,
        saving: false,
        saveError: '',
        getFormState: this.getFormState,
        saveSection: this.saveSection,
        cancelEdit: this.cancelEdit,
      };
    },
    formattedCareType() {
      return routeCareTypeToFormattedCareType(this.careType);
    },
    profile() {
      return this.profileData?.profiles?.[this.formattedCareType]?.profile || {};
    },
    formattedEducation() {
      return {
        levelOfEducation: this.profile.education?.levelOfEducation?.toUpperCase() || null,
        major: this.profile.education?.major || null,
        schoolName: this.profile.education?.schoolName || null,
        yearGraduated: this.profile.education?.yearGraduated || null,
        currentlyAttending: this.profile.education?.currentlyAttending || false,
      };
    },
    formattedUpsertData() {
      const formattedEducation = {
        levelOfEducation: this.profile.education?.levelOfEducation?.toUpperCase() || null,
        major: this.profile.education?.major || null,
        schoolName: this.profile.education?.schoolName || null,
        yearGraduated: this.profile.education?.yearGraduated || null,
        currentlyAttending: this.profile.education?.currentlyAttending || false,
      };
      if (this.formattedCareType === 'housekeeping') {
        const formattedPayRate = {
          min: this.profile.payRate?.min || 1500,
          max: this.profile.payRate?.max || 2500,
        };
        return {
          bio: this.profile.bio || '',
          comfortableWithPets: this.profile.comfortableWithPets || false,
          distanceWillingToTravel: this.profile.distanceWillingToTravel || 10,
          education: this.formattedEducation,
          experience: this.profile.servicesExperience || [],
          languagesSpoken: this.profile.languagesSpoken || [],
          paymentRate: formattedPayRate,
          services: this.profile.typesOfServices || [],
          yearsExperience: this.profile.yearsExperience ?? 0,
        };
      }
      if (this.formattedCareType === 'babysitter') {
        const formattedPayRate = {
          min: this.profile.payRate?.min || 1500,
          max: this.profile.payRate?.max || 2500,
        };
        return {
          accessToTransportation: this.profile.accessToTransportation || false,
          additionalServices: this.profile.additionalServices || [],
          ageGroups: this.profile.ageGroups || [],
          bio: this.profile.bio || '',
          comfortableWithPets: this.profile.comfortableWithPets || false,
          distanceWillingToTravel: this.profile.distanceWillingToTravel || 10,
          education: formattedEducation,
          languagesSpoken: this.profile.languagesSpoken || [],
          childWithSpecialNeeds: this.profile.childWithSpecialNeeds || false,
          homeworkHelp: this.profile.homeworkHelp || false,
          homeworkHelpLanguages: this.profile.homeworkHelpLanguages || [],
          homeworkHelpSubjects: this.profile.homeworkHelpSubjects || [],
          homeworkHelpType: this.profile.homeworkHelpType || [],
          sickChildren: this.profile.sickChildren || false,
          twins: this.profile.twins || false,
          willingToDriveChildren: this.profile.willingToDriveChildren || false,
          willingToRunErrands: this.profile.willingToRunErrands || false,
          increasePerChildRate: this.profile.increasePerChildRate || null,
          infantCare: this.profile.infantCare || false,
          infantCareExperience: this.profile.infantCareExperience || null,
          infantCareServices: this.profile.infantCareServices || [],
          maxNumberOfChildren: this.profile.numberOfChildrenAtOneTime || null,
          paymentRate: formattedPayRate,
          qualifications: this.profile.qualifications || [],
          specialNeeds: this.profile.specialNeeds || false,
          specialNeedsCareExperience: this.profile.specialNeedsCareExperience || [],
          specialNeedsConditionExperience: this.profile.specialNeedsConditionExperience || [],
          specialNeedsRecipientAgeGroups: this.profile.specialNeedsRecipientAgeGroups || [],
          specialNeedsServiceExperience: this.profile.specialNeedsServiceExperience || [],
          specialNeedsYearsExperience: this.profile.specialNeedsYearsExperience || null,
          yearsExperience: this.profile.yearsExperience ?? 0,
        };
      }

      if (this.formattedCareType === 'petCare') {
        const formattedPetRates = this.profile.rates
          ? Object.entries(this.profile.rates)
              .filter(
                ([serviceKey, rate]) =>
                  serviceKey !== '__typename' && typeof serviceRateToServiceKey[serviceKey] === 'string' && rate
              )
              .map(([serviceKey, rate]) => ({
                service: serviceRateToServiceKey[serviceKey],
                rate,
              }))
          : [];
        return {
          bio: this.profile.bio || '',
          distanceWillingToTravel: this.profile.distanceWillingToTravel || 10,
          education: formattedEducation,
          experience: this.profile.experienceCaringFor || [],
          experienceOther: this.profile.experienceOther || '',
          languagesSpoken: this.profile.languagesSpoken || [],
          maxNumberOfPets: parseInt(this.profile.numberOfPetsAtOneTime, 10) || null,
          qualifications: this.profile.qualifications || [],
          serviceRates: formattedPetRates,
          yearsExperience: this.profile.yearsExperience ?? 0,
        };
      }
      if (this.formattedCareType === 'companionCare') {
        return {
          bio: this.profile.bio || '',
          comfortableWithPets: this.profile.comfortableWithPets || false,
          distanceWillingToTravel: this.profile.distanceWillingToTravel || 10,
          education: this.formattedEducation,
          ageGroups: this.profile.ageGroupsExperience || [],
          experience: [
            ...(this.profile.basicCareServicesExperience || []),
            ...(this.profile.personalCareServicesExperience || []),
          ],
          languagesSpoken: [...new Set(this.profile.languagesSpoken || [])],
          qualifications: [...new Set(this.profile.qualifications || [])],
          serviceRates: serviceRateFormEntriesToUpsert(ratesToServiceRateFormEntries(this.profile.rates)),
          yearsExperience: this.profile.yearsExperience ?? 0,
        };
      }
      if (this.formattedCareType === 'nanny' || this.formattedCareType === 'babysitter') {
        // Required GraphQL fields only. Optional values are sent by the section that edits them.
        return {
          bio: this.profile.bio || '',
          comfortableWithPets: this.profile.comfortableWithPets || false,
          distanceWillingToTravel: this.profile.distanceWillingToTravel || 10,
          ageGroups: uniqueStringList(this.profile.ageGroups),
          qualifications: uniqueStringList([
            ...(this.profile.qualifications || []),
            ...(this.profile.childCareQualifications || []),
          ]),
          yearsExperience: this.profile.yearsExperience ?? 0,
          paymentRate: {
            min: this.profile.payRate?.min || 1500,
            max: this.profile.payRate?.max || 2500,
          },
        };
      }
      return {};
    },
  },
  methods: {
    getFormState() {
      if (!this.profileData) {
        return {
          bio: '',
          comfortableWithPets: false,
          typesOfServices: [],
          paymentRateMin: 15,
          paymentRateMax: 25,
          distanceWillingToTravel: 10,
          yearsExperience: 0,
          servicesExperience: [],
        };
      }

      const formState = {
        bio: this.profile.bio || '',
        distanceWillingToTravel: this.profile.distanceWillingToTravel || 10,
        education: {
          levelOfEducation: this.profile.education?.levelOfEducation || null,
          major: this.profile.education?.major || '',
          schoolName: this.profile.education?.schoolName || '',
          yearGraduated: this.profile.education?.yearGraduated || null,
          currentlyAttending: this.profile.education?.currentlyAttending || false,
        },
        languagesSpoken: this.profile.languagesSpoken || [],
        yearsExperience: this.profile.yearsExperience ?? 0,
      } as any;

      if (this.formattedCareType === 'housekeeping') {
        formState.comfortableWithPets = this.profile.comfortableWithPets || false;
        formState.typesOfServices = this.profile.typesOfServices || [];
        formState.paymentRateMin = this.profile.payRate?.min ? this.profile.payRate.min / 100 : 15;
        formState.paymentRateMax = this.profile.payRate?.max ? this.profile.payRate.max / 100 : 25;
        formState.servicesExperience = this.profile.servicesExperience || [];
      }

      if (this.formattedCareType === 'petCare') {
        formState.experience = this.profile.experienceCaringFor;
        formState.experienceOther = this.profile.experienceOther || '';
        formState.maxNumberOfPets = this.profile.numberOfPetsAtOneTime || 1;
        formState.qualifications = this.profile.qualifications || [];
        formState.serviceRates = this.profile.rates || [];
      }

      if (this.formattedCareType === 'babysitter') {
        formState.accessToTransportation = this.profile.accessToTransportation || false;
        formState.additionalServices = this.profile.additionalServices || [];
        formState.ageGroups = this.profile.ageGroups || [];
        formState.childWithSpecialNeeds = this.profile.childWithSpecialNeeds || false;
        formState.comfortableWithPets = this.profile.comfortableWithPets || false;
        formState.homeworkHelp = this.profile.homeworkHelp || false;
        formState.homeworkHelpLanguages = this.profile.homeworkHelpLanguages || [];
        formState.homeworkHelpSubjects = this.profile.homeworkHelpSubjects || [];
        formState.homeworkHelpType = this.profile.homeworkHelpType || [];
        formState.sickChildren = this.profile.additionalServices?.includes('SICK_CARE') || false;
        formState.twins = this.profile.additionalServices?.includes('TWINS') || false;
        formState.willingToDriveChildren = this.profile.additionalServices?.includes('DRIVE_CHILDREN') || false;
        formState.willingToRunErrands = this.profile.additionalServices?.includes('RUN_ERRANDS') || false;
        formState.increasePerChildRate = this.profile.increasePerChildRate || null;
        formState.infantCare = this.profile.infantCare || false;
        formState.infantCareExperience = this.profile.infantCareExperience || null;
        formState.infantCareServices = this.profile.infantCareServices || [];
        formState.maxNumberOfChildren = this.profile.numberOfChildrenAtOneTime || null;
        formState.paymentRate = this.profile.payRate || null;
        formState.qualifications = this.profile.qualifications || [];
        formState.specialNeeds = this.profile.specialNeeds || false;
        formState.specialNeedsCareExperience = this.profile.specialNeedsCareExperience || [];
        formState.specialNeedsConditionExperience = this.profile.specialNeedsConditionExperience || [];
        formState.specialNeedsRecipientAgeGroups = this.profile.specialNeedsRecipientAgeGroups || [];
        formState.specialNeedsServiceExperience = this.profile.specialNeedsServiceExperience || [];
        formState.specialNeedsYearsExperience = this.profile.specialNeedsYearsExperience || null;
      }

      if (this.formattedCareType === 'companionCare') {
        formState.comfortableWithPets = this.profile.comfortableWithPets || false;
        formState.willingToProvideTransportation = this.profile.willingToProvideTransportation || false;
        formState.willingToRunErrands = this.profile.willingToRunErrands || false;
        formState.individualizedServices = this.profile.individualizedServices || [];
        formState.healthAndMedicalServices = this.profile.healthAndMedicalServices || [];
        formState.services = this.profile.services || [];
        formState.ageGroupsExperience = this.profile.ageGroupsExperience || [];
        formState.basicCareServicesExperience = this.profile.basicCareServicesExperience || [];
        formState.personalCareServicesExperience = this.profile.personalCareServicesExperience || [];
        formState.rates = this.profile.rates || null;
        formState.qualifications = [...new Set(this.profile.qualifications || [])];
      }

      if (this.formattedCareType === 'nanny') {
        formState.comfortableWithPets = this.profile.comfortableWithPets || false;
        formState.ageGroups = uniqueStringList(this.profile.ageGroups);
        formState.maxNumberOfChildren = this.profile.numberOfChildrenAtOneTime || null;
        formState.paymentRateMin = this.profile.payRate?.min ? this.profile.payRate.min / 100 : 15;
        formState.paymentRateMax = this.profile.payRate?.max ? this.profile.payRate.max / 100 : 25;
        formState.paymentRate = this.profile.payRate || null;
        formState.increasePerChildRate = this.profile.increasePerChildRate ?? null;
        formState.accessToTransportation = this.profile.accessToTransportation || false;
        formState.validDriversLicense = this.profile.validDriversLicense || false;
        formState.homeworkHelp = this.profile.homeworkHelp || false;
        formState.homeworkHelpType = uniqueStringList(this.profile.homeworkHelpType);
        formState.homeworkHelpSubjects = uniqueStringList(this.profile.homeworkHelpSubjects);
        formState.specialNeeds = this.profile.specialNeeds || false;
        formState.additionalServices = uniqueStringList(this.profile.additionalServices);
        formState.infantCare = this.profile.infantCare || false;
        formState.infantCareExperience = this.profile.infantCareExperience || null;
        formState.infantCareServices = uniqueStringList(this.profile.infantCareServices);
        formState.qualifications = uniqueStringList([
          ...(this.profile.qualifications || []),
          ...(this.profile.childCareQualifications || []),
        ]);
        formState.childCareQualifications = uniqueStringList(this.profile.childCareQualifications);
        formState.childWithSpecialNeeds = this.profile.childWithSpecialNeeds || false;
        formState.specialNeedsRecipientAgeGroups = uniqueStringList(this.profile.specialNeedsRecipientAgeGroups);
        formState.specialNeedsYearsExperience = this.profile.specialNeedsYearsExperience || null;
        formState.specialNeedsCareExperience = uniqueStringList(this.profile.specialNeedsCareExperience);
        formState.specialNeedsConditionExperience = uniqueStringList(this.profile.specialNeedsConditionExperience);
        formState.specialNeedsServiceExperience = uniqueStringList(this.profile.specialNeedsServiceExperience);
        formState.sickChildren = this.profile.additionalServices?.includes('SICK_CARE') || false;
        formState.twins = this.profile.additionalServices?.includes('TWINS') || false;
        formState.willingToDriveChildren = this.profile.additionalServices?.includes('DRIVE_CHILDREN') || false;
        formState.willingToRunErrands = this.profile.additionalServices?.includes('RUN_ERRANDS') || false;
      }

      return formState;
    },
    async saveSection(partial) {
      this.errorUpdating = false;
      this.successUpdating = false;
      console.log('Attempting to save section with partial data:', partial); // eslint-disable-line no-console
      try {
        const upsertData = { ...this.formattedUpsertData, ...partial };
        const isChildCare = this.formattedCareType === 'nanny' || this.formattedCareType === 'babysitter';
        const careTypePayload =
          this.formattedCareType === 'companionCare'
            ? COMPANION_CARE_UPSERT_KEYS.reduce(
                (payload, key) => {
                  if (upsertData[key] === undefined) return payload;
                  const value =
                    key === 'qualifications' || key === 'languagesSpoken' || key === 'services'
                      ? [...new Set(upsertData[key] || [])]
                      : upsertData[key];
                  return { ...payload, [key]: value };
                },
                {} as Record<string, any>
              )
            : isChildCare
              ? CHILD_CARE_UPSERT_KEYS.reduce(
                  (payload, key) => {
                    if (upsertData[key] === undefined) return payload;
                    const value = CHILD_CARE_ARRAY_KEYS.has(key) ? uniqueStringList(upsertData[key]) : upsertData[key];
                    return { ...payload, [key]: value };
                  },
                  {} as Record<string, any>
                )
              : upsertData;
        await this.$apollo.mutate({
          mutation: UPSERT_PROFILE_SITTER,
          variables: {
            profile: {
              [this.graphqlCareType]: careTypePayload,
            },
          },
        });
        this.successUpdating = true;
        return { success: true };
      } catch (error) {
        const errorMessage = error.message ? `: ${error.message}` : '. Please try again.';
        this.errorMessage = `There was an error saving your changes${errorMessage}`;
        this.errorUpdating = true;
        console.log(error, error.message); // eslint-disable-line no-console
        throw error;
      }
    },
    cancelEdit() {
      const sitterUuid = this.$store.getters['account/getAccount']?.id;
      this.$router.push(`/provider/profile/${this.$route.params.care_type}/${sitterUuid}`);
    },
  },
  apollo: {
    profileData: {
      query() {
        return this.profileQuery;
      },
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          accountId: this.accountId,
        };
      },
      skip() {
        return !this.accountId;
      },
      update(data) {
        return data.account;
      },
      result() {
        this.loadingProfile = false;
      },
      error(error) {
        console.error('Error loading profile:', error);
        this.loadingProfile = false;
        this.loadError = true;
      },
    },
  },
};
</script>
